import React from "react"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { MetricHeaderWrapper } from "../ResourceBudget/Atom"
import { useNavigate } from "react-router-dom"
import { colors, containedButtonStyles } from "../../../styles/commonStyles"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"
import { getTokenLocal } from "../../../helper/utils"
import PNLTable from "../../components/Table/PNLBudgetTable"
import { getPnlBudget, getYears, updateBudget } from "./apiUtils"
import {
  clearDirtyList,
  setBudgetData,
  setRigidBudgetData,
  setSaved,
  setSelectedYear,
  setYears,
} from "../../redux/slices/pnlBudgetSlice"
import { fetchClassesHandler, fetchGroupsHandler } from "../Huddle/apiUtils"
import { setMaevaGroups, setMaevaClasses } from "../../redux/slices/metricSlice"
import { NavigationAssistant } from "../Huddle/Atom"
import PNLBudgetHeader from "../../components/Headers/PNLBudgetHeader"

function PNLBudget() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const token = getTokenLocal() as string

  const [saving, setSaving] = React.useState<boolean>(false)
  const yearInitializedRef = React.useRef<boolean>(false)
  const state = useAppSelector((state) => state.pnlBudget)
  const metricState: any = useAppSelector((store) => store.metric)

  const { maevaGroups, maevaClasses } = metricState
  const maevaSelectedGroups = metricState.maevaSelectedGroupsNoAll
  const maevaSelectedClasses = metricState.maevaSelectedClassesNoAll

  const { selectedYear, saved } = state

  const saveHandler = () => {
    updateBudget({
      state,
      selectedClass: maevaSelectedClasses?.[0],
      token,
      dispatch: (payload: any) => {
        dispatch(setSaved(!saved))
        dispatch(clearDirtyList(null))
      },
      loading: (loadVal: boolean) => {
        setSaving(loadVal)
        dispatch(setLoading(loadVal))
      },
    })
  }
  const loading = (loadVal: boolean) => dispatch(setLoading(loadVal))

  // Close sidebar when component mounts
  React.useEffect(() => {
    dispatch(setOpenSidebar(false))
  }, [])

  React.useEffect(() => {
    fetchGroupsHandler({
      token,
      dispatch: (payload: any) => {
        dispatch(setMaevaGroups(payload))
      },
      navigate,
      loading,
    })
  }, []) //? get groups

  React.useEffect(() => {
    if (maevaSelectedGroups?.length) {
      fetchClassesHandler({
        token,
        dispatch: (payload: any) => {
          dispatch(setMaevaClasses(payload))
        },
        navigate,
        group: maevaSelectedGroups?.[0],
        loading,
      })
    }
  }, [maevaSelectedGroups]) //? get classes with group sk

  React.useEffect(() => {
    // Fetch years when component loads
    console.log("PNL Budget: Fetching years on component mount")
    getYears({
      token,
      dispatch: (payload: any) => {
        console.log("PNL Budget: Years received:", payload)
        dispatch(setYears(payload))
      },
      navigate,
      loading,
    })
  }, []) // Fetch years on component mount

  // Set current year as default when years are loaded (only once)
  // Note: Years are sorted in descending order, but we specifically search for current year
  // to ensure it's selected even if it's not the first in the list
  React.useEffect(() => {
    if (state.years?.length && !selectedYear && !yearInitializedRef.current) {
      const currentYear = new Date().getFullYear()
      // Search for current year in the sorted list (works regardless of sort order)
      // Convert both to numbers for reliable comparison to handle string/number formats
      const currentYearOption = state.years.find((year: any) => {
        const yearValue = typeof year.label === 'number' ? year.label : parseInt(year.label)
        return yearValue === currentYear
      })
      if (currentYearOption) {
        // Select current year if found
        dispatch(setSelectedYear(currentYearOption))
      } else if (state.years.length > 0) {
        // If current year not found, select the first available year (most recent due to descending sort)
        dispatch(setSelectedYear(state.years[0]))
      }
      yearInitializedRef.current = true
    }
  }, [state.years]) // Only depend on state.years to prevent reset

  React.useEffect(() => {
    if (selectedYear && maevaSelectedClasses?.length) {
      dispatch(setLoading(true))
      getPnlBudget({
        token,
        dispatch: (data: any) => {
          dispatch(setBudgetData(data))
          dispatch(setRigidBudgetData(data))
        },
        loading: (loadVal: boolean) => dispatch(setLoading(loadVal)),
        group: maevaSelectedGroups?.[0],
        selectedClass: maevaSelectedClasses?.[0],
        year: selectedYear,
      })
    } else {
      // If conditions are not met, ensure loading is false
      dispatch(setLoading(false))
    }
  }, [selectedYear, maevaSelectedClasses, saved]) // Get budget data when year and class are selected

  const navigationData = React.useMemo(
    () => [
      {
        label: "Select a group to proceed",
        value: maevaSelectedGroups?.length ? true : false,
      },
      {
        label: "Select a class to proceed",
        value: maevaSelectedClasses?.length ? true : false,
      },
      {
        label: "Select a year to proceed",
        value: selectedYear ? true : false,
      },
    ],
    [maevaSelectedGroups, maevaSelectedClasses, selectedYear],
  )

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.background,
      }}
    >
      <PNLBudgetHeader saving={saving} saveHandler={saveHandler} />
      {selectedYear && maevaSelectedClasses?.length ? (
        <Box
          sx={{
            padding: "0px 20px",
            display: "flex",
            flexGrow: 1,
            flexDirection: "column",
            position: "relative",
          }}
        >
          <PNLTable />
        </Box>
      ) : (
        <NavigationAssistant data={navigationData} />
      )}
    </Box>
  )
}

export default PNLBudget
